import { LightSerialPort, StringDataListener, type Sensor } from "./core";
import { DeviceType, DzPresenceSensor, X001APresenceSensor, type DeviceTypeEventsMap } from "./sensors";

export class SerialPort<T extends DeviceType> {
  private _baudRate = 9600;
  private _baseUrl: string = "http://localhost:8080";
  private _encoding?: string;
  private _pid?: string;
  private _portName?: string;
  private _terminator: Uint8Array = new Uint8Array([0x0d, 0x0a]);
  private _timeout: number = 2000;
  private _type: T;
  private _vid?: string;

  private constructor(type: T) {
    this._type = type;
  }

  static create<T extends DeviceType>(type: T): SerialPort<T> {
    return new SerialPort<T>(type);
  }

  baudRate(baudRate: number): this {
    this._baudRate = baudRate;
    return this;
  }

  baseUrl(baseUrl: string): this {
    this._baseUrl = baseUrl;
    return this;
  }

  private createSensor(dataListener: StringDataListener): Sensor<DeviceTypeEventsMap[T]> {
    switch (this._type) {
      case DeviceType.NEXMOSPHERE_PRESENCE_XY_116_146_174:
        return new X001APresenceSensor(dataListener) as unknown as Sensor<DeviceTypeEventsMap[T]>;
      case DeviceType.NEXMOSPHERE_PRESENCE_AND_AIR_BUTTON_XY_240_241_251:
        return new DzPresenceSensor(dataListener) as unknown as Sensor<DeviceTypeEventsMap[T]>;
      default:
        throw new Error(`Unsupported device type: ${this._type}`);
    }
  }

  async open(): Promise<{ sensor: Sensor<DeviceTypeEventsMap[T]>; dispose: () => Promise<void> }> {
    if (this._baseUrl == null) {
      throw new Error('Base URL is not set');
    }
    const port = new LightSerialPort({
      baseUrl: this._baseUrl,
      baudRate: this._baudRate,
      pid: this._pid,
      portName: this._portName,
      terminator: this._terminator,
      vid: this._vid,
    });
    await port.open();
    const dataListener = new StringDataListener(port, {
      decoder: new TextDecoder(this._encoding),
      readTimeout: this._timeout
    });
    const sensor = this.createSensor(dataListener);
    return {
      sensor,
      async dispose() {
        sensor.dispose();
        await port.close();
      }
    };
  }

  portName(portName: string): this {
    this._portName = portName;
    return this;
  }

  pid(pid: string): this {
    this._pid = pid;
    return this;
  }

  encoding(encoding: string): this {
    this._encoding = encoding;
    return this;
  }

  terminator(terminator: string): this {
    this._terminator = new TextEncoder().encode(terminator);
    return this;
  }

  timeout(timeout: number): this {
    this._timeout = timeout;
    return this;
  }

  vid(vid: string): this {
    this._vid = vid;
    return this;
  }
}
